import { useCallback } from 'react';
import logger from '../../../utils/logger';
import { getCommentRecordId, getReplyRecordId } from '../../../utils/commentIds';
import { triggerLikeCommentNotification } from '../../../utils/triggerLikeCommentNotification';

const matchesComment = (comment, index, commentId, commentIndex) => {
  const cid = getCommentRecordId(comment);
  const byId =
    commentId != null &&
    cid != null &&
    String(cid) === String(commentId);
  const byIndex = commentIndex != null && index === commentIndex;
  return byId || byIndex;
};

/**
 * SongPage comment/reply like handlers. Keeps `songDetails.comments` in sync
 * optimistically (CommentList reads from songDetails, not SongContext feed)
 * and delegates persistence + notifications to SongContext callables.
 */
export function useSongCommentLikeActions({
  setSongDetails,
  likeComment,
  unlikeComment,
  likeReply,
  unlikeReply,
}) {
  const patchCommentLike = useCallback(
    (commentId, commentIndex, userId, liked) => {
      setSongDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          comments: (prev.comments || []).map((comment, index) => {
            if (!matchesComment(comment, index, commentId, commentIndex)) {
              return comment;
            }
            const likes = comment.likes || [];
            return {
              ...comment,
              likes: liked
                ? [...new Set([...likes, userId])]
                : likes.filter((id) => id !== userId),
            };
          }),
        };
      });
    },
    [setSongDetails]
  );

  const patchReplyLike = useCallback(
    (commentId, commentIndex, replyId, replyIndex, userId, liked) => {
      setSongDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          comments: (prev.comments || []).map((comment, index) => {
            if (!matchesComment(comment, index, commentId, commentIndex)) {
              return comment;
            }
            return {
              ...comment,
              replies: (comment.replies || []).map((reply, rIndex) => {
                const rid = getReplyRecordId(reply);
                const byId =
                  replyId != null &&
                  rid != null &&
                  String(rid) === String(replyId);
                const byIndex = replyIndex != null && rIndex === replyIndex;
                if (!byId && !byIndex) return reply;
                const likes = reply.likes || [];
                return {
                  ...reply,
                  likes: liked
                    ? [...new Set([...likes, userId])]
                    : likes.filter((id) => id !== userId),
                };
              }),
            };
          }),
        };
      });
    },
    [setSongDetails]
  );

  const handleLikeComment = useCallback(
    async (songId, commentId, userId, commentIndex = null) => {
      patchCommentLike(commentId, commentIndex, userId, true);
      try {
        const result = await likeComment(songId, commentId, userId, commentIndex);
        void triggerLikeCommentNotification({
          songId,
          commentId:
            result?.resolvedCommentId != null
              ? String(result.resolvedCommentId)
              : commentId != null
                ? String(commentId)
                : null,
          commentIndex: result?.commentIndex ?? commentIndex,
          isReply: false,
        });
      } catch (error) {
        patchCommentLike(commentId, commentIndex, userId, false);
        logger.error('Error liking comment:', error);
        throw error;
      }
    },
    [likeComment, patchCommentLike]
  );

  const handleUnlikeComment = useCallback(
    async (songId, commentId, userId, commentIndex = null) => {
      patchCommentLike(commentId, commentIndex, userId, false);
      try {
        await unlikeComment(songId, commentId, userId, commentIndex);
      } catch (error) {
        patchCommentLike(commentId, commentIndex, userId, true);
        logger.error('Error unliking comment:', error);
        throw error;
      }
    },
    [unlikeComment, patchCommentLike]
  );

  const handleLikeReply = useCallback(
    async (
      songId,
      commentId,
      replyId,
      userId,
      commentIndex = null,
      replyIndex = null
    ) => {
      patchReplyLike(commentId, commentIndex, replyId, replyIndex, userId, true);
      try {
        const result = await likeReply(
          songId,
          commentId,
          replyId,
          userId,
          commentIndex,
          replyIndex
        );
        void triggerLikeCommentNotification({
          songId,
          commentId:
            result?.resolvedCommentId != null
              ? String(result.resolvedCommentId)
              : commentId != null
                ? String(commentId)
                : null,
          commentIndex: result?.commentIndex ?? commentIndex,
          isReply: true,
          replyIndex: result?.replyIndex ?? replyIndex,
          replyId:
            result?.resolvedReplyId != null
              ? String(result.resolvedReplyId)
              : replyId != null
                ? String(replyId)
                : null,
        });
      } catch (error) {
        patchReplyLike(
          commentId,
          commentIndex,
          replyId,
          replyIndex,
          userId,
          false
        );
        logger.error('Error liking reply:', error);
        throw error;
      }
    },
    [likeReply, patchReplyLike]
  );

  const handleUnlikeReply = useCallback(
    async (
      songId,
      commentId,
      replyId,
      userId,
      commentIndex = null,
      replyIndex = null
    ) => {
      patchReplyLike(
        commentId,
        commentIndex,
        replyId,
        replyIndex,
        userId,
        false
      );
      try {
        await unlikeReply(
          songId,
          commentId,
          replyId,
          userId,
          commentIndex,
          replyIndex
        );
      } catch (error) {
        patchReplyLike(
          commentId,
          commentIndex,
          replyId,
          replyIndex,
          userId,
          true
        );
        logger.error('Error unliking reply:', error);
        throw error;
      }
    },
    [unlikeReply, patchReplyLike]
  );

  return {
    handleLikeComment,
    handleUnlikeComment,
    handleLikeReply,
    handleUnlikeReply,
  };
}
